import React, { useRef, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Multi-select from a fixed reference list. value = array of `value` strings.
export function MultiSelect({ options = [], value = [], onChange, placeholder = "Select…", testId, max }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  // Inside a Dialog/Sheet the scroll lock kills touch scrolling in portalled content, so the
  // list is rendered INSIDE the dialog — and then constrained to the dialog's box, because a
  // transformed, scrollable dialog clips anything that sticks out past its edges.
  const host = open ? triggerRef.current?.closest('[role="dialog"]') || undefined : undefined;
  const toggle = (val) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else {
      if (max && value.length >= max) return;
      onChange([...value, val]);
    }
  };
  const labelFor = (val) => options.find((o) => o.value === val)?.label || val;

  return (
    <div data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" ref={triggerRef} aria-label={placeholder || "Choose options"}
            className="flex min-h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 py-2 text-left text-sm hover:bg-secondary/40">
            <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
              {value.length === 0 ? placeholder : `${value.length} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="flex max-h-[min(60dvh,var(--radix-popover-content-available-height))] w-[--radix-popover-trigger-width] flex-col overflow-hidden p-0"
          align="start" collisionPadding={12} container={host} collisionBoundary={host}>
          <Command className="flex min-h-0 max-h-full flex-col">
            <CommandInput placeholder="Search…" />
            {/* ONE scroll container, sized to the space Radix actually has (so the first rows
                are never clipped when the list opens upwards), with momentum scrolling. */}
            <CommandList className="min-h-0 max-h-none flex-1 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]">
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)} className="py-2.5 sm:py-1.5" data-testid={testId ? `${testId}-option-${o.value}` : undefined}>
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value.includes(o.value) ? "opacity-100 text-primary" : "opacity-0")} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((val) => (
            <Badge key={val} variant="secondary" className="gap-1 bg-teal-50 text-teal-700 hover:bg-teal-100">
              {labelFor(val)}
              <button type="button" onClick={() => toggle(val)} className="ml-0.5"
                aria-label={`Remove ${labelFor(val)}`}>
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
